import type { DynamicFilterGroup } from '@/models/common/dynamicFilter/DynamicFilterGroup';
import { RestManager } from '@/newApi/services/rest/RestManager';
import { getApiHost } from '@/appSettings';
import type {
    DataSources,
    SuggestionInputText,
    FilteredSuggestionInput,
    FilterBoxPlotInput,
    FilterPlotInput,
    KeyValueInput,
    KeyValueResponse,
    TooltipInput,
    TooltipOut,
    UniqueOut,
    AutomaticAnalysisResult,
    SuggestionInputTextEmployment,
    EmploymentSuggestion,
    FriendSuggestion,
    FilterLineInput,
    KeyValueObj,
    FilteredSuggestionInputMultiple,
    InputFieldNames,
    UniqueOutMultiple,
    DynamicFilterGroup as AnalyticsFilterGroup,
    UniqueResultIntervals,
    SuggestionInputResultInterval,
    BlinderOaxacaSingleInput,
    AutomaticAnalysisOldInput,
    MahalaInput,
    MahalaResult,
    AutomaticAnalysisOldResult,
    FieldInfo,
    BlinderOaxacaGroupResult2
} from '@sysarb/sysarb-types/services/v1/analytics';
import type { Guid } from 'guid-typescript';
import type { AxiosRequestConfig } from 'axios';
import type { AutomatedAnalysisSettings } from '@/models/analytics/AutomatedAnalysis';
import type { StorageFilterGroup } from '@sysarb/sysarb-types/services/v1/filter-storage';
import type { useResgate } from '@/login/composables/useResgate';
import { useLoginStore } from '@/login/stores/useLoginStore';
import type { MarketSalaryStatsInput, MarketSalaryStatsResponse } from '@sysarb/sysarb-types/services/v1/analytics/exports';
import type { CareerType } from '@sysarb/sysarb-types/services/v1/job-architecture';

export class Analytics {
    private readonly analyticsService = new RestManager(`https://${getApiHost()}/analytics-service`);

    public abort(): void {
        this.analyticsService.abort();
    }

    public async getChartData<T>(payload: FilterPlotInput | FilterBoxPlotInput | FilterLineInput, chartType: string, options?: AxiosRequestConfig): Promise<T> {
        const { instance } = useLoginStore();
        return this.analyticsService.post<T>(
            payload,
            `v1/${instance}/graphs/${chartType.toLowerCase()}`,
            undefined,
            options
        );
    }

    public async getChartTooltipData(id: string | Guid, dataSource: DataSources, fields: string[]): Promise<TooltipOut> {
        const data: TooltipInput = {
            id: id.toString(),
            dataSource,
            fields
        };
        const { instance } = useLoginStore();
        return this.analyticsService.post<TooltipOut>(data, `v1/${instance}/tooltip`);
    }

    /**
     * Fetches key values from a datasource
     */
    public async getKeyValuesData(filter: StorageFilterGroup, keyValues: KeyValueObj[], dataSource: DataSources, groupBy: string = 'nogroup', chunkSize?: number, options?: AxiosRequestConfig): Promise<KeyValueResponse> {
        const data: KeyValueInput = {
            filter,
            keyValues,
            groupBy,
            dataSource,
            chunkSize
        };

        const { instance } = useLoginStore();
        return this.analyticsService.post<KeyValueResponse>(data, `v1/${instance}/keyValues`, undefined, options);
    }

    /**
     * Fetches suggestions for mixMax values
     */
    public async getUniqueFieldAutoCompleteValues(resgate: ReturnType<typeof useResgate>, field: string, dataSource: DataSources, currentInput?: string, limit?: number): Promise<UniqueOut | null> {
        const data: SuggestionInputText = {
            field: field as InputFieldNames,
            dataSource,
            limit,
            currentInput,
        };
        return resgate.call<UniqueOut>('analytics-service.v1.field-suggestions', 'unique', data).then(response => {
            if (response?.success === false) return null;
            return response.data;
        });
    }

    public async getCustomerNameAutoCompleteValues(field: string, dataSource: DataSources, currentInput?: string, limit?: number): Promise<FriendSuggestion[] | null> {
        const data: SuggestionInputText = {
            field: field as InputFieldNames,
            dataSource,
            currentInput,
            limit
        };

        const { instance } = useLoginStore();
        return this.analyticsService.post<FriendSuggestion[]>(data, `v1/${instance}/suggestions/friends`);
    }

    /**
     * Fetches suggestions by id.
     * @returns EmploymentSuggestion[]
     */
    public async getIdFieldAutoCompleteValues(dataSource: DataSources, sourceId?: string, currentInput?: string, limit?: number): Promise<EmploymentSuggestion[]> {
        const data: SuggestionInputTextEmployment = {
            dataSource,
            sourceId,
            currentInput,
            limit
        };

        const { instance } = useLoginStore();
        return this.analyticsService.post<EmploymentSuggestion[]>(data, `v1/${instance}/suggestions/employments`);
    }

    /**
     * Fetches suggestions for unique values after filter is applied
     */
    public async getUniqueFieldValuesFiltered(field: string, dataSource: DataSources, filter?: DynamicFilterGroup): Promise<UniqueOut> {
        const data: FilteredSuggestionInput = {
            field: field as InputFieldNames,
            dataSource,
            filter: filter != null ? filter.toRequestModel() : undefined
        };
        const { instance } = useLoginStore();
        return this.analyticsService.post<UniqueOut>(data, `v1/${instance}/suggestions/filtered`);
    }

    /**
     * Fetches info about the values in a field after filtering the field
     */
    public async getFieldInfo(field: string, dataSource: DataSources, filter?: AnalyticsFilterGroup): Promise<FieldInfo> {
        const data: FilteredSuggestionInput = {
            field: field as InputFieldNames,
            dataSource,
            filter: filter ?? undefined
        };
        const { instance } = useLoginStore();
        return this.analyticsService.post<FieldInfo>(data, `v1/${instance}/suggestions/info`);
    }

    /**
     * Fetches point data for mapping work group
     */
    public async getAutomatedAnalysis(groupid: Guid, settings: AutomatedAnalysisSettings): Promise<AutomaticAnalysisResult> {
        const data = {
            ageWeight: settings.ageWeight,
            employmentTimeWeight: settings.employmentTimeWeight,
            salaryWeight: settings.salaryWeight,
            useMedian: settings.useMedian,
            groupIds: [groupid.toString()],
            baseEthnicity: settings.baseEthnicity,
        };
        const { instance } = useLoginStore();
        return this.analyticsService.post<AutomaticAnalysisResult>(data, `v1/${instance}/automatic-analysis/singleworks`);
    }

    /**
     * Fetches point data for mapping work group
     */
    public async getAutomatedAnalysisOld(input: AutomaticAnalysisOldInput): Promise<AutomaticAnalysisOldResult> {
        const { instance } = useLoginStore();
        return this.analyticsService.post<AutomaticAnalysisOldResult>(input, `v1/${instance}/automatic-analysis/old/singleworks`);
    }

    public async getBlinderOaxacaAnalysis(input: BlinderOaxacaSingleInput): Promise<BlinderOaxacaGroupResult2 | null> {
        const { instance } = useLoginStore();
        if (instance === null) {
            throw new Error('Service string is null when trying to get blinder oaxaca analysis.');
        }
        return this.analyticsService.post<BlinderOaxacaGroupResult2>(input, `v2/${instance}/blinder-oaxaca`).catch((e: unknown) => {
            console.error(e);
            return null;
        });
    }

    public async getMahalanobisDistances(input: MahalaInput): Promise<MahalaResult | null> {
        const { instance } = useLoginStore();
        return this.analyticsService.post<MahalaResult>(input, `v1/${instance}/mahalanobis`);
    }

    public async getMultipleFieldsAutocompleteValues(fields: InputFieldNames[], dataSource: DataSources, filter?: AnalyticsFilterGroup): Promise<UniqueOutMultiple> {
        const data: FilteredSuggestionInputMultiple = {
            fields,
            dataSource,
            filter
        };

        const { instance } = useLoginStore();
        return this.analyticsService.post<UniqueOutMultiple>(data, `v1/${instance}/suggestions/filtered/multiple`);
    }

    public async getResultIntervalSuggestions(salaryReviewId: string): Promise<UniqueResultIntervals> {
        const data: SuggestionInputResultInterval = {
            salaryReviewId
        };

        const { instance } = useLoginStore();
        return this.analyticsService.post<UniqueResultIntervals>(data, `v1/${instance}/suggestions/result-intervals`);
    }

    /**
     * Fetches market salary statistics for a specific job profile
     * @param jobFamilyId Global job family ID
     * @param country Country code (e.g., "NOR", "SWE")
     * @param profileLevel Job valuation level
     * @param jobCareerType Career type code (e.g., "IND", "MAN", "PRO")
     * @param sector Industry sector (e.g., "public", "private")
     * @returns Salary statistics or null if below minimum threshold
     */
    public async getMarketSalaryStats(
        jobFamilyId: string,
        country: string,
        profileLevel: number,
        jobCareerType: CareerType,
        sector: string
    ): Promise<MarketSalaryStatsResponse | null> {
        const data: MarketSalaryStatsInput = {
            jobFamilyId,
            country,
            profileLevel,
            jobCareerType,
            sector
        };

        const { instance } = useLoginStore();
        return this.analyticsService.post<MarketSalaryStatsResponse | null>(data, `v1/${instance}/keyvalues/market-salary-stats`);
    }
}
